import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { INoPhaseTicketForm, ISimpleEventDetail, ISubTicketForm, ISubTicketItem, ITicketPhasesForm, ProductType, SupportedLanguage } from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { SubTicketForm } from "../../forms/subTicketForm";
import { useCreate, useTranslation, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { DropdownMenu } from "components/dropdownMenu";
import { NoPhaseTicketForm } from "../../forms/noPhaseTicketForm";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { formatNumber } from "utills/helpers";
import { TooltipCircle } from "components/tooltipCircle";
import { ConfirmModal } from "components/confirmModal";
import { FullScreenLoading } from "components/fullScreenLoading";
import { endpointService } from "components/shared/services/endpointService";

interface SubTicketModal {
  open: boolean;
  submit: (data: ISubTicketForm) => void;
  defaultValues: ISubTicketItem | null;
}

interface SubTicketsProps {
  ticketIndex: number;
  eventDetailData: ISimpleEventDetail;
  phaseTicket?: boolean;
  _moveField?: (order: string[], ticketMovedId: string) => void;
  setOpenLogModalId: (id: string) => void;
}

interface NoPhaseTicketModal {
  open: boolean;
  submit: (data: INoPhaseTicketForm) => void;
  defaultValues: ISubTicketItem | null;
}

const SUB_TICKET_MODAL_CONFIG: SubTicketModal = {
  open: false,
  submit: (data: ISubTicketForm) => {},
  defaultValues: null,
}

const NO_PHASE_TICKET_MODAL_CONFIG: NoPhaseTicketModal = {
  open: false,
  submit: (data: INoPhaseTicketForm) => {},
  defaultValues: null,
}

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {}
}

export const SubTickets = ({ticketIndex, eventDetailData, phaseTicket = true, _moveField, setOpenLogModalId}: SubTicketsProps) => {
  const { translate }= useTranslation();
  const [subTicketModalConfig, setSubTicketModalConfig] = useState(SUB_TICKET_MODAL_CONFIG);
  const [noPhaseTicketModalConfig, setNoPhaseTicketModalConfig] = useState(NO_PHASE_TICKET_MODAL_CONFIG);
  const [subTicketConfirmModalConfig, setSubTicketConfirmModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  const { mutate, isLoading: isCreating } = useCreate();
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdate();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ITicketPhasesForm>(); // retrieve all hook methods

  const { fields, append, move } = useFieldArray({
    control,
    name: `group_tickets.${ticketIndex}.sub_tickets`,
    keyName: "field_id"
  });

  useEffect(() => {
    if (draggedItemId) {
      _moveField && _moveField(fields.map(field => field.id ?? ""), draggedItemId);
      setDraggedItemId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedItemId])

  const _onCloseModal = () => {
    setSubTicketModalConfig(SUB_TICKET_MODAL_CONFIG);
  }

  const _onCloseNoPhaseTicketModal = () => {
    setNoPhaseTicketModalConfig(NO_PHASE_TICKET_MODAL_CONFIG);
  }

  const _onCloseSubTicketConfirmModal = () => {
    setSubTicketConfirmModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: subTicketConfirmModalConfig.title
    });
  }

  const _createSubTicket = (submitValues: ISubTicketForm) => {
    const ticketId = get(watch("group_tickets")[ticketIndex].ticket, "id", "");
    mutate(
      {
        resource: endpointService.getEventEndpoint("createSubTicket"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          parent_ticket_id: ticketId,
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const newSubTicket = get(data, "data.data", null) as ISubTicketItem;
          append(newSubTicket);
          toast.success(translate("toastMessage.addedSuccessfully"));
          _onCloseModal();
        },
      }
    );
  }

  const _updateSubTicket = (subTicketIndex: number, submitValues: ISubTicketForm) => {
    const ticketId = get(watch("group_tickets")[ticketIndex].sub_tickets[subTicketIndex], "id", "");
    updateMutation(
      {
        resource: endpointService.getEventEndpoint("updateSubTicket", {
          id: ticketId ?? ""
        }),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const updatedSubTicket = get(data, "data.data", null) as ISubTicketItem;
          if (updatedSubTicket) {
            setValue(`group_tickets.${ticketIndex}.sub_tickets.${subTicketIndex}`, updatedSubTicket);
          }
          toast.success(translate("toastMessage.addedSuccessfully"));
          _onCloseModal();
        },
      }
    );
  }

  const _updateNoPhaseTicket = (id: string, submitValues: INoPhaseTicketForm) => {
    updateMutation(
      {
        resource:endpointService.getEventEndpoint("updateSingleTicket", {
          id,
        }),
        id : "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const updatedTicket = get(data, "data.data", null) as ISubTicketItem;
          console.log(updatedTicket);
          if (updatedTicket) {
            const updatedTicketIndex = subTicketsData.findIndex(subTicket => get(subTicket, `id`, '') === id);
            setValue(`group_tickets.${ticketIndex}.sub_tickets.${updatedTicketIndex}`, updatedTicket);
          }
          toast.success(translate("toastMessage.addedSuccessfully"));
          _onCloseNoPhaseTicketModal();
        },
      }
    );
  }

  const handleDrag = ({ source, destination }: any) => {
    if (destination && (source.index !== destination.index)) {
      move(source.index, destination.index);
      setDraggedItemId(fields[source.index].id ?? null);
    }
  };

  const handleClickMove = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex !== destinationIndex) {
      move(sourceIndex, destinationIndex);
      setDraggedItemId(fields[sourceIndex].id ?? null);
    }
  }

  const subTicketsData = watch("group_tickets")[ticketIndex].sub_tickets;

  const isPackage = watch("group_tickets")[ticketIndex].ticket?.is_package ?? false;

  const ticketPhases = watch("ticket_phases");

  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN) ? "en" : eventDetailData.languages[0] ?? 'vi';

  return (
    <>
      <DragDropContext onDragEnd={handleDrag}>
        <div className="grid sm:grid-cols-6 lg:gap-x-6">
          <Droppable droppableId="subticket-items" isDropDisabled={isUpdating}>
            {(provided, snapshot) => (
              <div
                className="col-span-full"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {fields.map((subTicketField, subTicketIndex: number) => (
                  <Draggable
                    key={`subticket[${subTicketIndex}]`}
                    draggableId={`item-${subTicketIndex}`}
                    isDragDisabled={phaseTicket || isUpdating} // disable in phase ticket
                    index={subTicketIndex}
                  >
                    {(provided, snapshot) => (
                      <div
                        className={`border rounded-lg mb-4 ${
                          get(errors, `group_tickets.${subTicketIndex}`, "")
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        key={subTicketField.field_id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div
                          className="box-container p-0"
                          {...provided.dragHandleProps}
                        >
                          <div className="w-full flex items-start gap-x-3 px-4 py-4 sm:px-6 hover:cursor-pointer">
                            <div className="flex-1 flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 sm:gap-3">
                              <p
                                className="flex-1 font-bold"
                                style={{ wordBreak: "break-word" }}
                              >
                                {!isEmpty(ticketPhases)
                                  ? get(
                                      ticketPhases.find(
                                        (phase) =>
                                          phase.id ===
                                          get(
                                            subTicketsData[subTicketIndex],
                                            `ticket_phase`,
                                            ""
                                          )
                                      )?.name,
                                      firstLanguage,
                                      ""
                                    )
                                  : get(
                                      subTicketsData[subTicketIndex],
                                      `category.${firstLanguage}`,
                                      ""
                                    )}
                              </p>
                              <div className="flex-1 flex flex-row items-center xl:justify-end gap-x-3 text-sm">
                                <div className="flex flex-wrap items-center xl:justify-end gap-x-3 gap-y-2">
                                  <p>
                                    {get(
                                      subTicketsData[subTicketIndex],
                                      "sell_from",
                                      ""
                                    )
                                      ? moment(
                                          get(
                                            subTicketsData[subTicketIndex],
                                            "sell_from",
                                            ""
                                          )
                                        ).format("HH:mm DD/MM/YYYY")
                                      : ""}{" "}
                                    -{" "}
                                    {get(
                                      subTicketsData[subTicketIndex],
                                      "sell_to",
                                      ""
                                    )
                                      ? moment(
                                          get(
                                            subTicketsData[subTicketIndex],
                                            "sell_to",
                                            ""
                                          )
                                        ).format("HH:mm DD/MM/YYYY")
                                      : ""}
                                  </p>
                                  <p>
                                    {formatNumber(
                                      get(
                                        subTicketsData[subTicketIndex],
                                        "price",
                                        0
                                      )
                                    )}{" "}
                                    {eventDetailData.currency}{" "}
                                    <TooltipCircle
                                      text={`${translate(
                                        "eventSetup.phaseAndTicket.originalPrice"
                                      )}: ${formatNumber(
                                        get(
                                          subTicketsData[subTicketIndex],
                                          "original_price",
                                          0
                                        )
                                      )} ${
                                        eventDetailData.currency
                                      } <br/>${translate(
                                        "eventSetup.phaseAndTicket.sellPrice"
                                      )}: ${formatNumber(
                                        get(
                                          subTicketsData[subTicketIndex],
                                          "price",
                                          0
                                        )
                                      )} ${eventDetailData.currency}`}
                                    />
                                  </p>
                                  <div className="shrink-0 flex flex-row gap-x-2">
                                    {subTicketsData[subTicketIndex]
                                      ?.is_active ? (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                                      >
                                        {translate("active")}
                                      </span>
                                    ) : (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                                      >
                                        {translate("in-active")}
                                      </span>
                                    )}
                                    {subTicketsData[subTicketIndex]
                                      ?.is_active ? (
                                      subTicketsData[subTicketIndex]
                                        ?.hide_in_ui ? (
                                        <span
                                          className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                                        >
                                          {translate("hide")}
                                        </span>
                                      ) : (
                                        <span
                                          className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                                        >
                                          {translate("show")}
                                        </span>
                                      )
                                    ) : (
                                      <></>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 self-center">
                              <DropdownMenu
                                label={
                                  <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                }
                                items={[
                                  {
                                    label: translate("boxAction.edit"),
                                    onClick: () =>
                                      phaseTicket
                                        ? setSubTicketModalConfig({
                                            open: true,
                                            submit: (data) =>
                                              _updateSubTicket(
                                                subTicketIndex,
                                                data
                                              ),
                                            defaultValues:
                                              subTicketsData[subTicketIndex],
                                          })
                                        : setNoPhaseTicketModalConfig({
                                            open: true,
                                            defaultValues:
                                              subTicketsData[subTicketIndex],
                                            submit: (data) =>
                                              _updateNoPhaseTicket(
                                                get(
                                                  subTicketsData[
                                                    subTicketIndex
                                                  ],
                                                  `id`,
                                                  ""
                                                ),
                                                data
                                              ),
                                          }),
                                  },
                                  {
                                    label: subTicketsData[subTicketIndex]
                                      ?.is_active
                                      ? translate("deactivate")
                                      : translate("activate"),
                                    onClick: () =>
                                      setSubTicketConfirmModalConfig({
                                        open: true,
                                        title: translate("modal.toggleStatus", {
                                          status: subTicketsData[subTicketIndex]
                                            ?.is_active
                                            ? translate("deactivate")
                                            : translate("activate"),
                                          object: phaseTicket
                                            ? translate(
                                                "eventSetup.phaseAndTicket.subTicket"
                                              )
                                            : translate(
                                                "eventSetup.phaseAndTicket.ticket"
                                              ),
                                        }),
                                        submit: () => {
                                          phaseTicket
                                            ? _updateSubTicket(subTicketIndex, {
                                                ...subTicketsData[
                                                  subTicketIndex
                                                ],
                                                is_active:
                                                  !subTicketsData[
                                                    subTicketIndex
                                                  ]?.is_active,
                                              })
                                            : _updateNoPhaseTicket(
                                                get(
                                                  subTicketsData[
                                                    subTicketIndex
                                                  ],
                                                  `id`,
                                                  ""
                                                ),
                                                {
                                                  ...(subTicketsData[
                                                    subTicketIndex
                                                  ] as INoPhaseTicketForm),
                                                  is_active:
                                                    !subTicketsData[
                                                      subTicketIndex
                                                    ].is_active,
                                                }
                                              );
                                        },
                                      }),
                                  },
                                  ...(subTicketsData[subTicketIndex]?.is_active
                                    ? [
                                        {
                                          label: subTicketsData[subTicketIndex]
                                            ?.hide_in_ui
                                            ? translate("show")
                                            : translate("hide"),
                                          onClick: () =>
                                            setSubTicketConfirmModalConfig({
                                              open: true,
                                              title: translate(
                                                "modal.toggleStatus",
                                                {
                                                  status: subTicketsData[
                                                    subTicketIndex
                                                  ]?.hide_in_ui
                                                    ? translate("show")
                                                    : translate("hide"),
                                                  object: phaseTicket
                                                    ? translate(
                                                        "eventSetup.phaseAndTicket.subTicket"
                                                      )
                                                    : translate(
                                                        "eventSetup.phaseAndTicket.ticket"
                                                      ),
                                                }
                                              ),
                                              submit: () => {
                                                phaseTicket
                                                  ? _updateSubTicket(
                                                      subTicketIndex,
                                                      {
                                                        ...subTicketsData[
                                                          subTicketIndex
                                                        ],
                                                        hide_in_ui:
                                                          !subTicketsData[
                                                            subTicketIndex
                                                          ]?.hide_in_ui,
                                                      }
                                                    )
                                                  : _updateNoPhaseTicket(
                                                      get(
                                                        subTicketsData[
                                                          subTicketIndex
                                                        ],
                                                        `id`,
                                                        ""
                                                      ),
                                                      {
                                                        ...(subTicketsData[
                                                          subTicketIndex
                                                        ] as INoPhaseTicketForm),
                                                        hide_in_ui:
                                                          !subTicketsData[
                                                            subTicketIndex
                                                          ].hide_in_ui,
                                                      }
                                                    );
                                              },
                                            }),
                                        },
                                      ]
                                    : []),
                                  ...(subTicketIndex !== 0 && !phaseTicket
                                    ? [
                                        {
                                          label: translate("boxAction.moveUp"),
                                          onClick: () =>
                                            handleClickMove(
                                              subTicketIndex,
                                              subTicketIndex - 1
                                            ),
                                        },
                                      ]
                                    : []),
                                  ...(subTicketIndex !== fields.length - 1 &&
                                  !phaseTicket
                                    ? [
                                        {
                                          label:
                                            translate("boxAction.moveDown"),
                                          onClick: () =>
                                            handleClickMove(
                                              subTicketIndex,
                                              subTicketIndex + 1
                                            ),
                                        },
                                      ]
                                    : []),
                                  {
                                    label: "Log",
                                    onClick: () =>
                                      setOpenLogModalId(
                                        subTicketsData[subTicketIndex]?.id ?? ""
                                      ),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}

                <div className="text-center text-sm">
                  {isEmpty(fields) ? (
                    <p>{translate("eventSetup.phaseAndTicket.noTickets")}</p>
                  ) : (
                    <></>
                  )}
                </div>
                {phaseTicket ? (
                  <div className="w-full text-center">
                    <button
                      type="button"
                      className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
                      onClick={() =>
                        setSubTicketModalConfig({
                          open: true,
                          submit: (data) => _createSubTicket(data),
                          defaultValues: null,
                        })
                      }
                    >
                      <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                      <span className="shrink-0 text-sm font-medium">
                        {translate(
                          "eventSetup.phaseAndTicket.addMoreSubTicket"
                        )}
                      </span>
                    </button>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      {phaseTicket ? (
        <SubTicketForm
          eventDetailData={eventDetailData}
          isPackage={isPackage}
          open={subTicketModalConfig.open}
          onClose={_onCloseModal}
          ticketPhases={ticketPhases}
          title={
            subTicketModalConfig.defaultValues
              ? `${translate("edit")} ${translate(
                  "eventSetup.phaseAndTicket.subTicket"
                )}`
              : `${translate("add")} ${translate(
                  "eventSetup.phaseAndTicket.subTicket"
                )}`
          }
          defaultValues={subTicketModalConfig.defaultValues}
          onSubmit={subTicketModalConfig.submit}
        />
      ) : (
        <NoPhaseTicketForm
          defaultValues={noPhaseTicketModalConfig.defaultValues}
          eventDetailData={eventDetailData}
          open={noPhaseTicketModalConfig.open}
          onClose={_onCloseNoPhaseTicketModal}
          productType={ProductType.PAYMENT_GROUP}
          onSubmit={noPhaseTicketModalConfig.submit}
          title={`${translate("edit")} ${translate(
            "eventSetup.phaseAndTicket.ticket"
          )}`}
        />
      )}
      <ConfirmModal
        key="active-sub-ticket"
        title={subTicketConfirmModalConfig.title}
        content=""
        open={subTicketConfirmModalConfig.open}
        onConfirm={subTicketConfirmModalConfig.submit}
        onClose={_onCloseSubTicketConfirmModal}
      />
      {isCreating || isUpdating ? <FullScreenLoading /> : <></>}
    </>
  );
}